import { useRef, useState } from "react";
import type { Row } from "../types";
import { api, ApiError } from "../api";
import { IconClose, IconCheck, IconUpload, IconFile, IconAlert } from "../icons";

interface Props {
  treinamento: Row; // linha de treinamento
  onClose: () => void;
  onSent: () => void; // recarrega listas após enviar
}

/**
 * Anexa o PDF do CERTIFICADO de um treinamento. Ao enviar, o certificado é
 * guardado na pasta do colaborador (aqui) e repassado ao DOCUMENTO do
 * colaborador no sistema de DP (integração server-to-server).
 */
export default function CertificadoModal({ treinamento, onClose, onSent }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [dpAviso, setDpAviso] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function enviar() {
    if (!file) {
      setError("Selecione o arquivo do certificado (PDF).");
      return;
    }
    setSaving(true);
    setError(null);
    setDpAviso(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.treinamentos.enviarCertificado(treinamento.id, fd);
      if (res.dp?.enviado) {
        setOkMsg("Certificado salvo na pasta do colaborador e enviado ao documento dele no DP.");
      } else {
        // Salvou localmente, mas o DP não recebeu (colaborador não encontrado ou DP fora do ar).
        setOkMsg("Certificado salvo na pasta do colaborador.");
        setDpAviso(res.dp?.mensagem || "Não foi possível enviar ao DP.");
      }
      onSent();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao anexar o certificado");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>
            <IconUpload size={18} /> Anexar certificado
          </h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="concluir-resumo">
            <strong>{treinamento.titulo}</strong>
            <span>{treinamento.colaborador_nome || "Colaborador"}</span>
          </div>

          <div className="form-grid">
            <label className="field field-wide">
              <span>Certificado (PDF)</span>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,image/*"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
              {file && (
                <span className="file-chosen">
                  <IconFile size={15} /> {file.name}
                </span>
              )}
            </label>
          </div>

          <p className="hint">
            <IconUpload size={14} /> O certificado é guardado na <strong>pasta</strong> do colaborador e
            enviado ao <strong>documento dele no DP</strong> (casado por e-mail/nome).
          </p>

          {error && <div className="form-error">{error}</div>}
          {okMsg && (
            <div className="form-ok">
              <IconCheck size={16} /> {okMsg}
            </div>
          )}
          {dpAviso && (
            <div className="form-error" style={{ marginTop: 8 }}>
              <IconAlert size={16} /> {dpAviso}
            </div>
          )}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose}>
            {okMsg ? "Fechar" : "Cancelar"}
          </button>
          {!okMsg && (
            <button type="button" className="btn-primary" onClick={enviar} disabled={saving}>
              {saving ? "Enviando…" : "Anexar e enviar ao DP"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
